import React, { useCallback, useEffect, useState } from "react";

import { disableFieldModulesByFieldId, getFieldShapesAdmin, getPaymentItems } from "services";

import { formatNumber, formatNumericCurrency, useCancelAsyncOnUnmount } from "utils";

import {
  AdminDescriptions,
  AdminSection,
  Card,
  Col,
  Collapse,
  ErrorBoundary,
  Image,
  Link,
  MapInteractive,
  Modal,
  Panel,
  Row,
  Tabs,
  TagNormal,
  Tooltip,
  Message as message,
} from "components";

import { FarmFieldModuleBau } from "pages/admin/farms/components/FarmFieldModuleBau";
import { FarmFieldModuleYearlyData } from "pages/admin/farms/components/FarmFieldModuleYearlyData";
import { FarmFieldSocSamples } from "pages/admin/farms/components/FarmFieldSocSamples";

export const FarmFieldsTab = ({ farm, location, markers, user, loadFarm }) => {
  const [loading, setLoading] = useState(false);
  const [geometries, setGeometries] = useState([]);
  const signal = useCancelAsyncOnUnmount()();
  const [showModal, setShowModal] = useState(false);
  const [fieldIdToDisable, setFieldIdToDisable] = useState(null);
  const [disablingFieldModules, setDisablingFieldModules] = useState(false);

  const getShapes = useCallback(() => {
    getFieldShapesAdmin({
      farmId: farm.id,
      signal,
    })
      .then((res) => {
        setGeometries(res.fieldShapes);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [farm.id, signal]);

  useEffect(() => {
    getShapes();
  }, [farm.zip, getShapes]);

  const handleModuleDisableClick = (fieldId) => {
    setFieldIdToDisable(fieldId);
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setFieldIdToDisable(null);
  };

  const disableFieldModules = () => {
    setDisablingFieldModules(true);

    disableFieldModulesByFieldId({
      fieldId: fieldIdToDisable,
      signal,
    })
      .then(() => {
        message({
          content: "The gremlins accepted your sacrifice and will consume it! Nom nom nom",
          icon: "gremlin",
        });
        loadFarm(false);
        handleModalClose();
      })
      .finally(() => setDisablingFieldModules(false));
  };

  return (
    !loading && (
      <>
        {location && (
          <Row gutter={[{ xs: 8, sm: 8, md: 16 }, 32]}>
            <Col>
              <Card className="map-card">
                <MapInteractive
                  geometries={geometries}
                  location={location}
                  markers={[
                    ...markers,
                    {
                      location: location,
                      name: farm.name,
                    },
                  ]}
                  scaleControl={true}
                  zoomControl={true}
                />
              </Card>
            </Col>
          </Row>
        )}
        {farm && farm.fieldList && farm.fieldList.length > 0 ? (
          <Collapse className="margin-bottom-300">
            {farm.fieldList.map((field, i) => (
              <Panel
                header={
                  <>
                    {field.name ? field.name : "Unknown field name"}
                    <span className="small grey-text margin-left-100">
                      {formatNumber({ number: field.size, decimalPlaces: 4 })} ha{" "}
                      {field.modules.length > 0 && `| Modules: ${field.moduleStates.active} active - ${field.moduleStates.disabled} disabled`}
                    </span>
                  </>
                }
                key={i}
                id={`#${field.id}`}
                extra={
                  field.verified ? (
                    <Tooltip placement="top" title="This field is verified">
                      <span>
                        <TagNormal color="green" text="Verified" />
                      </span>
                    </Tooltip>
                  ) : (
                    <Tooltip placement="top" title="This field is not verified">
                      <span>
                        <TagNormal color="red" text="Unverified" />
                      </span>
                    </Tooltip>
                  )
                }
              >
                <Tabs
                  items={[
                    {
                      label: "Field details",
                      key: "field",
                      children: (
                        <>
                          <AdminDescriptions
                            items={[
                              {
                                span: 1,
                                label: "Field number",
                                children: field.number ? field.number : "-",
                              },
                              {
                                span: 1,
                                label: "Field size (ha)",
                                children: field.size ? formatNumber({ number: field.size, decimalPlaces: 4 }) : "-",
                              },
                              {
                                span: 1,
                                label: "Field location",
                                children: field.location ? `${field.location.lat} (lat), ${field.location.lng} (lng)` : "-",
                              },
                              {
                                span: 1,
                                label: "Nearby",
                                children: field.nearby.length > 0 ? field.nearby.map((item, i) => <span key={i}>{item} </span>) : "-",
                              },
                              {
                                span: 1,
                                label: "Soil type",
                                children: field.soilType ? field.soilType : "-",
                              },
                              {
                                span: 1,
                                label: "Soil points",
                                children: field.soilPoints ? field.soilPoints : "-",
                              },
                            ]}
                          />

                          <FarmFieldSocSamples fieldId={field.id} className="margin-top-200" />
                        </>
                      ),
                    },
                    {
                      label: "Field methods",
                      key: "methods",
                      children: field.methods.length > 0 && (
                        <Collapse>
                          {field.methods.map((method, k) => (
                            <Panel
                              header={method.title}
                              key={k}
                              extra={
                                method.completed ? (
                                  <Tooltip placement="top" title="This method was completed">
                                    <span>
                                      <TagNormal color="green" text="Completed" />
                                    </span>
                                  </Tooltip>
                                ) : (
                                  <Tooltip placement="top" title="This method is not completed">
                                    <span>
                                      <TagNormal color="orange" text="Active" />
                                    </span>
                                  </Tooltip>
                                )
                              }
                            >
                              <AdminSection headline="Method summary" />
                              <Row>
                                <Col>
                                  <AdminDescriptions
                                    items={[
                                      {
                                        span: 1,
                                        label: "Pending points",
                                        children: formatNumber({
                                          number: parseFloat(method.pendingPoints),
                                        }),
                                      },
                                      {
                                        span: 1,
                                        label: "Received points",
                                        children: formatNumber({
                                          number: parseFloat(method.receivedPoints),
                                        }),
                                      },
                                      {
                                        span: 1,
                                        label: "Funded points",
                                        children: formatNumber({
                                          number: parseFloat(method.fundedPoints),
                                        }),
                                      },
                                      {
                                        span: 1,
                                        label: "Funds",
                                        children: formatNumericCurrency(parseFloat(method.funds)),
                                      },
                                      {
                                        span: 1,
                                        label: "Pending tasks",
                                        children: method.tasks.filter((v) => !v.verified).length,
                                      },
                                      {
                                        span: 1,
                                        label: "Verified tasks",
                                        children: method.tasks.filter((v) => v.verified).length,
                                      },
                                    ]}
                                  />
                                </Col>
                              </Row>

                              {method.methodReview && (
                                <>
                                  <AdminSection headline="Method review" />
                                  <Row>
                                    <Col>
                                      <AdminDescriptions
                                        items={[
                                          {
                                            span: 3,
                                            label: "Overall, how satisfied were you with the result of the method?",
                                            children: method.completeMethodReviewSatisfaction,
                                          },
                                          {
                                            span: 3,
                                            label: "Has the method been economically viable for you?",
                                            children: method.completeMethodReviewEconomy,
                                          },
                                          {
                                            span: 3,
                                            label: "Did the right in the ground attract attention?",
                                            children: method.completeMethodReviewGround,
                                          },
                                        ]}
                                      />
                                    </Col>
                                  </Row>
                                </>
                              )}

                              <AdminSection headline="Onboarding answers" />
                              <Row>
                                <Col>
                                  <AdminDescriptions
                                    items={method.answers.map((answer) => ({
                                      span: 3,
                                      label: answer.question,
                                      children: Array.isArray(answer.answer) ? (
                                        <>
                                          {" "}
                                          {answer.answer.map((item, m) => {
                                            if (answer.answer.length > m + 1) {
                                              return <span key={m}>{item} | </span>;
                                            }
                                            return <span key={m}>{item}</span>;
                                          })}
                                        </>
                                      ) : answer.questionType === "image" ? (
                                        <div className="full-image">
                                          <Image src={answer.answer} />
                                        </div>
                                      ) : (
                                        <>{answer.answer}</>
                                      ),
                                    }))}
                                  />
                                </Col>
                              </Row>
                            </Panel>
                          ))}
                        </Collapse>
                      ),
                      disabled: !field.methods.length > 0,
                    },
                    {
                      label: "Field tasks",
                      key: "tasks",
                      children: field.methods.length > 0 && (
                        <>
                          {field.methods.map((method, j) => (
                            <AdminDescriptions
                              key={j}
                              items={method.tasks.map((task) => ({
                                span: 3,
                                label: task.title,
                                children: (
                                  <>
                                    {method.title} | {task.verified ? <>Verified on {task.verificationDate}</> : <>Not verified</>}
                                  </>
                                ),
                              }))}
                            />
                          ))}
                        </>
                      ),
                      disabled: !field.methods.length > 0,
                    },
                    {
                      label: "Field modules",
                      key: "field-modules",
                      children:
                        field.modules.length !== 0 ? (
                          <>
                            {field.modules.some(({ disabled }) => !disabled) && (user.role === "tier1" || user.role === "tier2") && (
                              <ErrorBoundary>
                                <VestingLost fieldModuleIds={field.modules.map(({ fieldModuleId }) => fieldModuleId)} />

                                <Link onClick={() => handleModuleDisableClick(field.id)} className="margin-bottom-200">
                                  Disable all modules of this field
                                </Link>
                              </ErrorBoundary>
                            )}

                            {field.modules.map((module) => {
                              const { completed, disabled } = module;
                              return (
                                <Collapse key={module.module}>
                                  <Panel
                                    header={module.module}
                                    extra={
                                      <>
                                        {disabled ? (
                                          <Tooltip placement="top" title="This field is not enabled">
                                            <span>
                                              <TagNormal color="red" text="Disabled" />
                                            </span>
                                          </Tooltip>
                                        ) : (
                                          <Tooltip placement="top" title="This field is enabled">
                                            <span>
                                              <TagNormal color="green" text="Enabled" />
                                            </span>
                                          </Tooltip>
                                        )}
                                        {completed ? (
                                          <Tooltip placement="top" title="This field is completed">
                                            <span>
                                              <TagNormal color="green" text="Completed" />
                                            </span>
                                          </Tooltip>
                                        ) : (
                                          <Tooltip placement="top" title="This field is not verified">
                                            <span>
                                              <TagNormal color="red" text="Not Completed" />
                                            </span>
                                          </Tooltip>
                                        )}
                                      </>
                                    }
                                  >
                                    <Row>
                                      <Col sm={24} md={24}>
                                        {user && module && <FarmFieldModuleBau module={module} farmId={farm?.id} />}
                                      </Col>
                                    </Row>
                                    <Row>
                                      <Col sm={24} md={24}>
                                        {module && <FarmFieldModuleYearlyData fieldModuleId={module.fieldModuleId} user={user} />}
                                      </Col>
                                    </Row>
                                  </Panel>
                                </Collapse>
                              );
                            })}
                          </>
                        ) : (
                          <AdminSection
                            headline="Field Without Modules"
                            description={
                              <>
                                <b>This field has no modules :(</b>
                              </>
                            }
                          />
                        ),
                    },
                  ]}
                />
              </Panel>
            ))}
          </Collapse>
        ) : (
          <AdminSection description="No methods yet :(" />
        )}

        <Modal
          cancelText="close"
          description={"Are you sure you want to disable all modules on this field? All Vesting, if exists, will be lost."}
          onOk={disableFieldModules}
          onCancel={handleModalClose}
          okText={"proceed"}
          showModal={showModal}
          title={"Disable field modules"}
          loading={disablingFieldModules}
        />
      </>
    )
  );
};

const VestingLost = ({ fieldModuleIds }) => {
  const [paymentItems, setPaymentItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchPaymentItems = useCallback(() => {
    getPaymentItems({
      vesting: true,
      paid: false,
      fieldModuleIds,
    })
      .then((res) => setPaymentItems(res))
      .finally(() => setLoading(false));
  }, [fieldModuleIds]);

  useEffect(() => {
    fetchPaymentItems();
  }, [fetchPaymentItems]);

  const vestingSum = paymentItems.reduce((prev, { payment }) => prev + payment, 0);

  if (loading) {
    return <p>Loading vesting...</p>;
  }

  if (vestingSum === 0) {
    return <p>No vesting lost</p>;
  }

  return <p>Vesting lost: {formatNumericCurrency(vestingSum)}</p>;
};
